/**
 * The ice of เหมันต์สะกดธาร (the snow-sword warp chain): the frost crescent each cut leaves, crystals
 * bursting from a foe, frost spreading on the ground under it, the ice a frozen foe is held in and
 * that ice shattering, frost gathering on the blade, the caster's frost afterimages and the frost
 * creeping in round the screen on the last cut.
 *
 * The sheets are fields of light drawn and cached by fxSheets.ts (`FRAMES` frames each, four hard
 * tones lit from the upper right) in the ice ramp — white highlight, pale cyan, a deep blue rim —
 * with see-through bodies so a frozen foe shows inside its ice. Everything here is drawn once and
 * kept: per frame the game only blits.
 */
import type { Def } from './fxSheets';
import { artX, artY, atRes, canvas, ctx2d, resOf } from './pixel';
import type { SwingStyle } from './skillFx';

type RGB = readonly [number, number, number];

/** Deep blue rim, pale cyan body, near-white lit faces, white glints. */
export const ICE_RAMP: { shade: RGB; body: RGB; light: RGB; core: RGB } = {
  shade: [34, 58, 118],
  body: [146, 212, 240],
  light: [222, 246, 255],
  core: [255, 255, 255],
};

/** The same ice as `r,g,b` strings, for particles and pixels drawn beside the sheets. */
export const ICE = {
  deep: '#223a76',
  cyan: '#92d4f0',
  pale: '#def6ff',
  white: '#ffffff',
} as const;

const TAU = Math.PI * 2;
const hash = (i: number): number => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const fadeAfter = (p: number, from: number) => (p < from ? 1 : 1 - (p - from) / (1 - from));
const easeOut = (x: number) => 1 - (1 - clamp01(x)) ** 2;

/** Is (u, v) inside the polygon? */
function inside(u: number, v: number, pts: readonly (readonly [number, number])[]): boolean {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!;
    const [xj, yj] = pts[j]!;
    if (yi > v !== yj > v && u < ((xj - xi) * (v - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

/**
 * A crystal: a long diamond centred at (cx, cy) along angle `a`, `len` long and `w` across at its
 * middle. Its face toward the upper right is lit, the other in the body tone; 0 outside.
 */
function crystal(u: number, v: number, cx: number, cy: number, a: number, len: number, w: number): number {
  const x = u - cx;
  const y = v - cy;
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const along = x * ca + y * sa;
  const across = -x * sa + y * ca;
  const half = len / 2;
  if (Math.abs(along) > half) return 0;
  if (Math.abs(across) > w * (1 - Math.abs(along) / half)) return 0;
  // Which side faces the light (up and to the right on screen).
  const lit = -sa * 1 + ca * -1 < 0 ? across < 0 : across > 0;
  return lit ? 0.9 : 0.5;
}

/** Sweep of each cut's crescent: from `a0` to `a1` round an ellipse (facing right, y down). */
const ARCS: Partial<Record<SwingStyle, { a0: number; a1: number; rx: number; ry: number; cy: number }>> = {
  snowRise: { a0: 1.25, a1: -1.65, rx: 22, ry: 21, cy: 0 },
  snowCleave: { a0: -2.2, a1: 1.15, rx: 23, ry: 22, cy: 0 },
  snowFinale: { a0: -2.4, a1: 1.3, rx: 28, ry: 26, cy: 0 },
  // A level sweep: round the near side of a flat ellipse, from behind to past the front.
  snowSweep: { a0: 3.0, a1: -0.75, rx: 30, ry: 14, cy: 5 },
  snowOpen: { a0: 3.0, a1: -0.75, rx: 33, ry: 15, cy: 5 },
};

/**
 * A cut's frost crescent: a keen white edge with a pale body, racing round (its head at the end of
 * the sweep a third of the way in) and draining after; behind the head, ice crystals grow off its
 * inner edge and linger after the light has gone. A thrust is a straight icy wedge instead.
 */
function arcDef(arc: SwingStyle): Def {
  if (arc === 'snowThrust') return thrustDef();
  const g = ARCS[arc] ?? ARCS.snowCleave!;
  const span = Math.abs(g.a1 - g.a0);
  const dirn = Math.sign(g.a1 - g.a0);
  const R = (g.rx + g.ry) / 2;
  const th = arc === 'snowFinale' ? 9 : 7;
  const spikes = Array.from({ length: 9 }, (_, i) => ({
    t: (i + 0.5) / 9,
    len: 4 + hash(i + 3) * 7,
    w: 1.7 + hash(i + 17) * 1,
  }));
  const m = 4;
  return {
    box: [g.rx + m, g.ry + m - g.cy, g.rx + m, g.ry + m + g.cy],
    outline: true,
    field: (p) => {
      const head = Math.min(1, p / 0.3);
      const tail = clamp01((p - 0.14) / 0.66);
      const fade = fadeAfter(p, 0.5);
      const grow = fadeAfter(p, 0.62);
      return (u, v) => {
        const ex = u / g.rx;
        const ey = (v - g.cy) / g.ry;
        const r = Math.hypot(ex, ey);
        if (r > 1.15 || r < 0.3) return 0;
        let d = dirn * (Math.atan2(ey, ex) - g.a0);
        d = ((d % TAU) + TAU) % TAU;
        const t = d / span;
        if (t > 1 || t > head) return 0;
        const dist = (1 - r) * R;
        if (t >= tail) {
          const along = (t - tail) / Math.max(1e-3, head - tail);
          const thick = 1 + th * Math.pow(along, 0.6) * fade;
          if (dist >= 0 && dist <= thick) {
            const x = dist / thick;
            return x < 0.3 ? 1 : x < 0.65 ? 0.7 : 0.45;
          }
        }
        // Crystals left on the trail, pointing in from the edge.
        for (const s of spikes) {
          if (s.t > head) continue;
          const lat = (t - s.t) * span * R;
          const L = s.len * grow;
          const depth = dist - 1.2;
          if (depth < 0 || depth > L || Math.abs(lat) > s.w * (1 - depth / L)) continue;
          return lat * dirn < 0 ? 0.7 : 0.45;
        }
        return 0;
      };
    },
  };
}

/** A thrust's frost: a long wedge of ice driven out ahead, crystals bristling along it. */
function thrustDef(): Def {
  const L = 48;
  return {
    box: [2, 12, L + 10, 12],
    outline: true,
    field: (p) => {
      const len = L * Math.min(1, p * 4);
      const fade = fadeAfter(p, 0.45);
      return (u, v) => {
        const x = u - 6;
        if (x < 0 || x > len + 2) return 0;
        const k = x / L;
        const half = (4.5 * (1 - k) + 1) * (0.4 + 0.6 * fade);
        const a = Math.abs(v);
        if (a <= half) return a < half * 0.35 ? 1 : v < 0 ? 0.7 : 0.45;
        // Crystals splaying back off the wedge, top and bottom in turn.
        for (let i = 0; i < 6; i++) {
          const cx = 8 + i * 7;
          if (cx > len) break;
          const up = i % 2 ? 1 : -1;
          const c = crystal(u - 6, v, cx, up * (half + 3), up * -2.4, (7 + hash(i) * 4) * fade, 1.9);
          if (c) return c;
        }
        return 0;
      };
    },
  };
}

/** Ice crystals bursting out of a struck foe, spinning out, slowing and falling. */
function shardsDef(): Def {
  const n = 10;
  const shards = Array.from({ length: n }, (_, k) => ({
    a: (k / n) * TAU + (hash(k + 1) - 0.5) * 0.5,
    sp: 0.6 + hash(k + 9) * 0.5,
    len: 7 + hash(k + 21) * 5,
    spin: (hash(k + 33) - 0.5) * 3,
  }));
  return {
    box: [34, 30, 34, 30],
    outline: true,
    field: (p) => {
      const fly = easeOut(p * 1.3);
      const shrink = fadeAfter(p, 0.55);
      const flash = p < 0.2 ? 6 * (1 - p / 0.2) : 0;
      return (u, v) => {
        if (flash && u * u + v * v < flash * flash) return 1;
        if (
          p < 0.3 &&
          ((Math.abs(u) < 0.8 && Math.abs(v) < 12 * (1 - p / 0.3)) ||
            (Math.abs(v) < 0.8 && Math.abs(u) < 16 * (1 - p / 0.3)))
        )
          return 1;
        for (const s of shards) {
          const r = 4 + 24 * s.sp * fly;
          const cx = Math.cos(s.a) * r;
          const cy = Math.sin(s.a) * r * 0.8 + p * p * 14;
          const c = crystal(u, v, cx, cy, s.a + s.spin * p, s.len * shrink, 2.2 * shrink + 0.4);
          if (c) return c;
        }
        return 0;
      };
    },
  };
}

/**
 * Frost spreading on the ground: a ragged patch of ice laid flat on the isometric ground (squashed
 * to half height in the field itself, so its pixels stay square), crystal lines across it, frost
 * feathers reaching out past its edge, then breaking up pixel by pixel as it melts.
 */
function frostDef(R: number): Def {
  const lobes = Array.from({ length: 4 }, (_, i) => ({
    f: 2 + i * 2 + (i > 1 ? 1 : 0),
    ph: hash(i + 40) * TAU,
  }));
  const feathers = Array.from({ length: 11 }, (_, i) => ({
    a: (i / 11) * TAU + hash(i + 60) * 0.4,
    len: 0.18 + hash(i + 80) * 0.22,
  }));
  return {
    box: [R * 1.4 + 2, R * 0.7 + 2, R * 1.4 + 2, R * 0.7 + 2],
    res: 1,
    outline: true,
    alpha: [0, 190, 120, 200, 235],
    field: (p) => {
      const grow = easeOut(p / 0.2);
      const melt = clamp01((p - 0.55) / 0.45);
      return (u, v0) => {
        const v = v0 * 2;
        // Melting: whole art pixels drop out, the rim first.
        const cell = hash(Math.floor(u) * 31.7 + Math.floor(v0) * 7.3);
        const r = Math.hypot(u, v);
        const a = Math.atan2(v, u);
        let edge = 0.78;
        for (const l of lobes) edge += Math.sin(a * l.f + l.ph) * 0.06;
        const Rr = R * grow * edge;
        const d = r / Math.max(0.5, Rr);
        if (melt > 0 && cell < melt * (0.6 + 0.6 * Math.min(1, d))) return 0;
        if (d <= 1) {
          if (d > 0.86) return 0.7;
          // Crystal lines: spokes from the middle and one ring.
          const spoke = Math.abs((((((a / TAU) * 7 + 0.5) % 1) + 1) % 1) - 0.5) * r;
          if (spoke < 0.8 && d > 0.15) return 0.7;
          if (Math.abs(d - 0.5) * Rr < 0.5) return 0.7;
          if (cell > 0.975) return 0.95;
          return 0.45;
        }
        for (const f of feathers) {
          const lat = ((a - f.a + TAU * 1.5) % TAU) - Math.PI;
          const out = d - 1;
          if (out < f.len * grow && Math.abs(lat) * r < 0.6) return 0.45;
        }
        return 0;
      };
    },
  };
}

/** The ice a frozen foe stands in (feet on the anchor): a cluster of tall crystals. */
const SHELL: readonly (readonly [number, number])[] = [
  [-11, 2],
  [-13, -18],
  [-11, -42],
  [-8, -52],
  [-4, -48],
  [-1, -63],
  [3, -55],
  [6, -59],
  [10, -46],
  [13, -22],
  [11, 2],
];
/** Crystals jutting out at its foot. */
const SHELL_FOOT = [
  [-12, 0, -2.2, 11],
  [12, 0, -0.9, 12],
  [-7, 2, -1.9, 8],
  [8, 2, -1.2, 7],
] as const;
/** Facet edges running up the ice, as (u at the foot, u at the top). */
const FACETS = [
  [-6, -4],
  [1, -1],
  [7, 5],
] as const;
/** Where the cracks start and the way each first runs. */
const CRACKS = [
  [2, -30, -2.4],
  [-4, -44, 0.6],
  [5, -14, -0.4],
  [-6, -8, 2.6],
  [0, -30, 0.9],
] as const;

/**
 * The ice: grown up from the ground over the first fifth of the effect, then held while cracks
 * spread across it toward the moment it breaks. The body is see-through (the foe shows inside),
 * the facets and the faces turned to the light pale, glints white.
 */
function shellDef(): Def {
  // Each crack as a zigzag of short runs, turning a little at each.
  const cracks = CRACKS.map(([x, y, a], i) => {
    const pts: [number, number][] = [[x, y]];
    let ang = a;
    let [cx, cy] = [x, y];
    for (let s = 0; s < 6; s++) {
      ang += (hash(i * 13 + s) - 0.5) * 1.6;
      cx += Math.cos(ang) * 4.5;
      cy += Math.sin(ang) * 4.5;
      pts.push([cx, cy]);
    }
    return pts;
  });
  const seg = (u: number, v: number, a: readonly number[], b: readonly number[]) => {
    const dx = b[0]! - a[0]!;
    const dy = b[1]! - a[1]!;
    const t = clamp01(((u - a[0]!) * dx + (v - a[1]!) * dy) / (dx * dx + dy * dy || 1));
    return Math.hypot(u - a[0]! - dx * t, v - a[1]! - dy * t);
  };
  return {
    box: [22, 66, 22, 5],
    outline: true,
    alpha: [0, 235, 96, 180, 255],
    field: (p) => {
      const form = clamp01(p / 0.2);
      const top = 2 - 68 * easeOut(form);
      const crack = clamp01((p - 0.28) / 0.72);
      return (u, v) => {
        if (v < top) return 0;
        let base = 0;
        if (inside(u, v, SHELL)) base = 0.42;
        else
          for (const [x, y, a, len] of SHELL_FOOT) {
            const c = crystal(u, v, x + Math.cos(a) * len * 0.4, y + Math.sin(a) * len * 0.4, a, len, 2.6);
            if (c) return c;
          }
        if (!base) return 0;
        // The freezing front climbing the ice, bright.
        if (form < 1 && v - top < 1.5) return 1;
        for (let i = 0; i < cracks.length; i++) {
          const pts = cracks[i]!;
          const reach = crack * (pts.length - 1) * (0.7 + 0.3 * hash(i + 90));
          for (let s = 0; s < pts.length - 1 && s < reach; s++) {
            const a = pts[s]!;
            const b0 = pts[s + 1]!;
            const k = Math.min(1, reach - s);
            const b = [a[0] + (b0[0] - a[0]) * k, a[1] + (b0[1] - a[1]) * k];
            const d = seg(u, v, a, b);
            if (d < 0.5) return 0.2;
            if (d < 1.1) return 0.95;
          }
        }
        for (const [f0, f1] of FACETS) {
          const fx = f0 + ((f1 - f0) * -v) / 60;
          if (Math.abs(u - fx) < 0.5) return 0.66;
        }
        // The faces toward the light, and a glint near the top right.
        if (u > 8) return 0.66;
        if ((u - 6) ** 2 + (v + 44) ** 2 < 2.5) return 1;
        return base;
      };
    },
  };
}

/** The ice breaking: a white flash through it, then its pieces flung out and falling. */
function shatterDef(): Def {
  const bits = Array.from({ length: 18 }, (_, k) => ({
    x: (hash(k + 3) - 0.5) * 24,
    y: -4 - hash(k + 7) * 54,
    a: hash(k + 11) * TAU,
    len: 6 + hash(k + 13) * 6,
    w: 2 + hash(k + 17) * 1.5,
    spin: (hash(k + 19) - 0.5) * 6,
    up: 10 + hash(k + 23) * 18,
  }));
  return {
    box: [46, 76, 46, 10],
    outline: true,
    field: (p) => {
      const flash = p < 0.08;
      const fly = easeOut(p * 1.2);
      const shrink = fadeAfter(p, 0.6);
      return (u, v) => {
        if (flash && inside(u, v, SHELL))
          return Math.abs(((u * 0.6 + v * 0.4) % 6) + 6) % 6 < 1 ? 0.95 : 0.42;
        for (const b of bits) {
          const out = b.x / 12;
          const cx = b.x + out * 40 * fly;
          // Thrown up and out, then falling.
          const cy = b.y - b.up * fly + 70 * p * p;
          if (cy > 4) continue;
          const c = crystal(u, v, cx, cy, b.a + b.spin * p, b.len * shrink, b.w * shrink + 0.3);
          if (c) return c;
        }
        return 0;
      };
    },
  };
}

/** Frost gathering on the blade: motes drawn in to a point, then a four-pointed glint. */
function glintDef(): Def {
  const motes = Array.from({ length: 7 }, (_, k) => ({
    a: (k / 7) * TAU + hash(k) * 0.6,
    r: 10 + hash(k + 5) * 5,
  }));
  return {
    box: [16, 16, 16, 16],
    field: (p) => {
      const pull = clamp01(p / 0.55);
      const star = clamp01((p - 0.45) / 0.55);
      const L = star > 0 ? 12 * Math.sin(star * Math.PI) : 0;
      return (u, v) => {
        if (L > 0) {
          const au = Math.abs(u);
          const av = Math.abs(v);
          if (u * u + v * v < 2.2) return 1;
          if (av < 0.6 && au < L) return au < L * 0.5 ? 1 : 0.7;
          if (au < 0.6 && av < L * 0.8) return av < L * 0.4 ? 1 : 0.7;
          if (Math.abs(au - av) < 0.5 && au < L * 0.3) return 0.7;
        }
        if (pull < 1)
          for (const m of motes) {
            const r = m.r * (1 - pull);
            const x = Math.cos(m.a + pull) * r;
            const y = Math.sin(m.a + pull) * r;
            if (Math.abs(u - x) < 0.9 && Math.abs(v - y) < 0.9) return 0.9;
          }
        return 0;
      };
    },
  };
}

/** The ice sheet of each kind (fxSheets.ts `defOf`). */
export function iceDef(
  kind: 'iceArc' | 'iceShards' | 'frost' | 'iceShell' | 'iceShatter' | 'frostGlint',
  arc: SwingStyle | undefined,
  r: number | undefined,
): Def {
  switch (kind) {
    case 'iceArc':
      return arcDef(arc ?? 'snowCleave');
    case 'iceShards':
      return shardsDef();
    case 'frost':
      return frostDef(r ?? 20);
    case 'iceShell':
      return shellDef();
    case 'iceShatter':
      return shatterDef();
    case 'frostGlint':
      return glintDef();
  }
}

const FROSTED = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
/**
 * A figure's frame turned to ice, for its afterimages: the deep blue rim round it, its darks pale
 * cyan, its lights near white (cached per frame, on the same art grid as the frame).
 */
export function frostedFrame(img: HTMLCanvasElement): HTMLCanvasElement {
  let c = FROSTED.get(img);
  if (c) return c;
  const W = img.width;
  const H = img.height;
  c = canvas(W, H);
  const g = ctx2d(c);
  g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, W, H);
  const a = d.data;
  const res = resOf(img);
  const out = new Uint8ClampedArray(a.length);
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && a[(y * W + x) * 4 + 3]! > 40;
  const tones: readonly RGB[] = [ICE_RAMP.shade, [104, 170, 222], ICE_RAMP.body, ICE_RAMP.light];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const o = (y * W + x) * 4;
      if (a[o + 3]! <= 40) continue;
      const rim = !solid(x - res, y) || !solid(x + res, y) || !solid(x, y - res) || !solid(x, y + res);
      const lum = (a[o]! * 0.3 + a[o + 1]! * 0.59 + a[o + 2]! * 0.11) / 255;
      const t = rim ? tones[0]! : lum > 0.6 ? tones[3]! : lum > 0.3 ? tones[2]! : tones[1]!;
      out[o] = t[0];
      out[o + 1] = t[1];
      out[o + 2] = t[2];
      out[o + 3] = 255;
    }
  d.data.set(out);
  g.putImageData(d, 0, 0);
  atRes(c, res, artX(img), artY(img));
  FROSTED.set(img, c);
  return c;
}

let vignette: { w: number; h: number; c: HTMLCanvasElement } | null = null;
/**
 * Frost creeping in from the edges of the screen (the last cut of เหมันต์สะกดธาร), `w` × `h` art pixels:
 * ragged ice crystals reaching in from every side in hard pixels, the middle clear. Drawn once per
 * screen size.
 */
export function frostVignette(w: number, h: number): HTMLCanvasElement {
  if (vignette && vignette.w === w && vignette.h === h) return vignette.c;
  const c = canvas(w, h);
  const g = ctx2d(c);
  const img = g.createImageData(w, h);
  const m = Math.max(8, Math.min(w, h) * 0.16);
  // Value noise on a coarse grid, for the ragged reach of the frost.
  const cell = 5;
  const n = (x: number, y: number) => {
    const ix = Math.floor(x / cell);
    const iy = Math.floor(y / cell);
    const fx = x / cell - ix;
    const fy = y / cell - iy;
    const h00 = hash(ix * 57 + iy * 131);
    const h10 = hash((ix + 1) * 57 + iy * 131);
    const h01 = hash(ix * 57 + (iy + 1) * 131);
    const h11 = hash((ix + 1) * 57 + (iy + 1) * 131);
    const top = h00 + (h10 - h00) * fx;
    return top + (h01 + (h11 - h01) * fx - top) * fy;
  };
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const d = Math.min(x, y, w - 1 - x, h - 1 - y) / m;
      const corner = Math.min(x, w - 1 - x) / m + Math.min(y, h - 1 - y) / m;
      const v =
        1.15 - Math.min(d, corner * 0.6) + (n(x, y) - 0.5) * 0.7 + (hash(x * 3 + y * 1777) - 0.5) * 0.12;
      const o = (y * w + x) * 4;
      const [col, a] =
        v > 0.95
          ? [ICE_RAMP.light, 170]
          : v > 0.78
            ? [ICE_RAMP.body, 120]
            : v > 0.68
              ? [ICE_RAMP.shade, 110]
              : [null, 0];
      if (!col) continue;
      img.data[o] = col[0];
      img.data[o + 1] = col[1];
      img.data[o + 2] = col[2];
      img.data[o + 3] = a;
    }
  g.putImageData(img, 0, 0);
  vignette = { w, h, c };
  return c;
}
